
const get = require('lodash/get')
const snakeCase = require('lodash/snakeCase')
const uuid = require('uuid/v4')
const xlsx = require('@sheet/core')
const { addHours, addDays } = require('date-fns')

const { ProxifiedEntityApi, UserRole } = require('../common')
const { wrapEntityApi } = require('../utils/wrap-api')
const { transformDecoratedRoutes } = require('./tools/transform-decorated-routes')
const { enrichRoutesWithVolumes } = require('./tools/enrich-routes-with-volumes')
const { generateRouteName } = require('../tools/utils')
const { transitionRoutePeriod } = require('../tools/location-edits')
const RoutePouchAdapter = require('./data-access/route-pouch-adapter')
const isRouteEditable = require('./tools/is-route-editable')
const RoutePGDataAdapter = require('./data-access/route-pg-data-adapter')
const RouteViewPGDataAdapter = require('./data-access/route-view-pg-data-adapter')
const toRelationalModel = require('./tools/to-relational-model')
const { toRouteCreatedAt } = require('./tools/route-created-at')
const { RouteStatus } = require('./tools')
const { StopMovingHistory } = require('./stop-moving-history')

const api = require('./api')
const { resolveWarehouseByLocationId } = require('../order/tools/read/warehouse-code-to-virtual')
const { getWarehouseOriginIdForUser } = require('./tools/get-warehouse-origin-id-for-user')

const ESTIMATED_DELIVERY_DATE_DAYS = 9
const ESTIMATED_PICKUP_DATE_HOURS = 48
const DEFAULT_LIMIT = 10
const DEFAULT_OFFSET = 0

/**
 * Parses limit/offset from filter, falling back to sensible defaults.
 * Avoids duplicating parseInt + isNaN logic across multiple methods.
 */
const parsePagination = (filter) => ({
  limit: parseInt(filter.limit, 10) || DEFAULT_LIMIT,
  offset: parseInt(filter.offset, 10) || DEFAULT_OFFSET
})

const rawMethods = {
  save: api.save,
  get: api.get,
  list: api.list,
  bulkImport: api.bulkImport
}
const entityName = 'route'

const methodsNeedingProxy = [
  'create',
  'update',
  {
    methodName: 'moveStop',
    restAction: 'create',
    autoRegister: true,
    needsUserData: true,
    permissions: [UserRole.National, UserRole.State, UserRole.DistributionManager]
  }
]

const isPlannerUser = (user) => {
  if (get(user, 'roles.length', 0) === 0) {
    return false
  }
  return user.roles.includes('feature:routePlanning:edit')
}

const isReviewerUser = (user) => {
  if (get(user, 'roles.length', 0) === 0) {
    return false
  }
  return user.roles.includes('feature:routePlanning:view')
}

const isCarrierUser = (user) => {
  const userRoles = get(user, 'roles', [])
  if (userRoles.length === 0) {
    return false
  }
  // We need to check in this weird way (checking if one of the roles contains that string)
  // because agave and web has slightly different roles names in the user object
  return userRoles.some(role => role.includes('userRole:psm-carrier'))
}

const isDriverUser = (user) => {
  const userRoles = get(user, 'roles', [])
  if (userRoles.length === 0) {
    return false
  }
  return userRoles.some(role => role.includes('userRole:psm-driver'))
}

class RoutesApi extends ProxifiedEntityApi {
  constructor (state, agaveAdapter, logger, pgConnection, mainApi) {
    const { user, routesDB } = state
    const adapter = new RoutePouchAdapter(user, routesDB)
    super(entityName, methodsNeedingProxy, !pgConnection, adapter, agaveAdapter)

    this.agaveAdapter = agaveAdapter

    if (pgConnection) {
      this.pgDataAdapter = new RoutePGDataAdapter(
        pgConnection,
        user,
        logger,
        { withGlobalAccessUser: state.withGlobalAccessUser }
      )

      this.pgRouteViewDataAdapter = new RouteViewPGDataAdapter(
        pgConnection,
        user,
        logger,
        { withGlobalAccessUser: state.withGlobalAccessUser }
      )

      this.pgConnection = pgConnection
    }

    this.mainApi = mainApi

    this.stopMovingHistoryAdapter = new StopMovingHistory(state, agaveAdapter, logger, pgConnection)

    const apiMethods = wrapEntityApi({
      save: api.save,
      bulkImport: api.bulkImport
    }, state)
    Object.assign(this, apiMethods)

    this.state = state
  }

  async moveStop ({ locationId, routeFromId, routeToId }) {
    if (!locationId || !routeFromId || !routeToId) {
      const err = new Error(`Cannot move stop: locationId, routeFromId, routeToId are required`)
      err.status = 400
      throw err
    }

    if (routeFromId === routeToId) {
      const err = new Error(`Cannot move stop: routeFromId and routeToId are the same`)
      err.status = 400
      throw err
    }

    const routeFrom = await this.get(routeFromId, { isOnline: true, includeDetails: false })

    if (!routeFrom) {
      const err = new Error(`Cannot move stop: route not found: ${routeFromId}`)
      err.status = 404
      throw err
    }

    const affectedShipments = routeFrom.shipments
      .filter(shipment => shipment.destination_id === locationId)

    // If the route changed after this operation was started and there are no shipments
    // connected to the location, we should not proceed with the operation
    if (affectedShipments.length === 0) {
      const err = new Error(`Cannot move stop: no shipments found for locationId: ${locationId} and routeFromId: ${routeFromId}`)
      err.status = 404
      throw err
    }

    const affectedShipmentIds = affectedShipments.map(shipment => shipment.shipment_id)
    const programIds = [...new Set(affectedShipments.map(s => s.program_id).filter(Boolean))]

    await Promise.all([
      this.mainApi.shipment.updateRoute({ shipmentIds: affectedShipmentIds, routeId: routeToId }),
      syncLocationRoute(this.state.locationsDB, locationId, routeToId, programIds)
    ])

    // Moving stops re-points shipments between routes, changing each route's
    // shipment-derived program/state/origin. Stamp updated_by/updated_at on both
    // routes so the audit trail stays accurate and, crucially, so a route left
    // with no shipments (orphaned) remains attributable to — and visible to —
    // the user who moved its stops away (see the route view's orphaned-route
    // visibility rule).
    if (this.pgDataAdapter) {
      await this.update({
        condition: { couchdb_id: [routeFromId, routeToId] },
        data: {}
      })
    }

    const stopMovingHistory = {
      location_id: locationId,
      route_from: routeFromId,
      route_to: routeToId,
      route_from_status: routeFrom.status
    }

    return this.stopMovingHistoryAdapter.create(stopMovingHistory)
  }

  /**
   * Getting the details of the route with all the related entities.
   * @param {Object} routeId - The id of the route.
   * @param {Object} [options] - Optional configuration object.
   * @param {boolean} [options.isDriverInfoVisibleToAll=false] - Whether driver information should be visible to all users(RMS specific).
   * @returns {Object} Route object decorated with shipments, locations and funders.
   */
  async getRouteDetails (routeId, { isDriverInfoVisibleToAll = false } = {}) {
    if (!routeId) {
      console.error('Cannot get route details: no routeId provided')
      return
    }
    try {
      return this.get(routeId, { isOnline: true, isDriverInfoVisibleToAll })
    } catch (e) {
      if (e.status === 404) {
        console.error('Cannot get route details: route not found', routeId)
        return
      }
      console.error('Error fetching route', routeId, e)
      throw e
    }
  }

  /**
   *
   * @param {Object} Lists all the routes from the database with the corresponding details.
   * @returns the list of routes with details matching the dates
   */
  async listAll ({ filter = {}, limit = 10, offset = 0 } = {}) {
    const { program: programIdFilter, warehouse: warehouseFilter, isDriverInfoVisibleToAll, includeDetails, ...restFilter } = filter
    const apiFilter = {
      ...restFilter,
      ...(programIdFilter ? { programIds: [programIdFilter] } : {}),
      ...(warehouseFilter ? { origin_id: warehouseFilter } : {}),
      limit,
      offset
    }
    try {
      return this.list({ ...apiFilter }, { isOnline: true, isDriverInfoVisibleToAll, includeDetails })
    } catch (e) {
      if (e.status === 404) {
        console.error('Cannot list routes: routes not found!')
        return
      }
      console.error('Error fetching routes', e)
      throw e
    }
  }

  async assignCarrier (routeIds, carrierId) {
    if (!carrierId) {
      throw new Error(`Cannot update carrier: carrierId is required parameter`)
    }
    if (!routeIds || !Array.isArray(routeIds) || !routeIds.length) {
      throw new Error(`Cannot update carrier: routeIds is required parameter or it can't be empty`)
    }

    // An emptied route (no shipments — cancelled or had all stops moved away) has
    // nothing to deliver, so it can't be assigned to a carrier (issue #20244 /
    // move-stops). Rather than reject the whole batch for one bad route, we skip
    // the emptied ones and assign the rest, returning which were skipped so the
    // caller can surface it.
    const { actionable, skipped } = await this._splitOutEmptiedRoutes(routeIds)
    if (!actionable.length) {
      return { updated: [], skipped }
    }

    await this._validateCarrierRouteAssignmentBasedOnOperationalStates(actionable, carrierId)

    const condition = { couchdb_id: actionable }
    const data = {
      carrier_id: carrierId
    }

    if (this.pgDataAdapter) {
      await this.update({ condition, data })
    } else {
      await this.update(undefined, { condition, data })
    }
    return { updated: actionable, skipped }
  }

  async unassignCarrier (routeIds) {
    if (!routeIds || !Array.isArray(routeIds) || !routeIds.length) {
      throw new Error(`Cannot unassign carrier: routeIds is required parameter or it can't be empty`)
    }

    const condition = { couchdb_id: routeIds }
    const data = { carrier_id: null }

    if (this.pgDataAdapter) {
      return this.update({ condition, data })
    }
    return this.update(undefined, { condition, data })
  }

  /**
   * Validates that a carrier can be assigned to routes based on operational states.
   * Throws an error if any route has shipments destined for states the carrier doesn't operate in.
   */
  async _validateCarrierRouteAssignmentBasedOnOperationalStates (routeIds, carrierId) {
    if (!get(this.mainApi, 'carrier.getOne') || !this.pgRouteViewDataAdapter) {
      return
    }

    try {
      const carrierData = await this.mainApi.carrier.getOne(carrierId)
      const operationalStates = get(carrierData, 'operationalStates', [])

      const carrierStateNames = operationalStates
        .map(opState => {
          const stateMatch = opState.state && opState.state.match(/:state:([^:]+)/)
          return stateMatch ? stateMatch[1].toLowerCase() : null
        })
        .filter(Boolean)

      if (carrierStateNames.length === 0) {
        throw new Error(
          `Cannot assign carrier "${get(carrierData, 'organisation.name', carrierId)}": ` +
          `Carrier has no operational states defined. Please verify carrier configuration.`
        )
      }

      // Fetch routes with their shipments to check destination states
      const { results: routes } = await this.pgRouteViewDataAdapter.getList({
        filter: { couchdb_id: routeIds },
        skipCount: true,
        limit: routeIds.length
      })

      // Check each route's shipment destinations
      const invalidRoutes = []
      for (const route of routes || []) {
        const shipments = route.shipments || []
        for (const shipment of shipments) {
          const destinationId = shipment.destination_id || ''
          const stateMatch = destinationId.match(/:state:([^:]+):/)
          if (stateMatch) {
            const shipmentState = stateMatch[1].toLowerCase()
            if (!carrierStateNames.includes(shipmentState)) {
              invalidRoutes.push({
                routeId: route.couchdb_id,
                routeName: route.name,
                destinationState: shipmentState
              })
              break
            }
          }
        }
      }

      if (invalidRoutes.length > 0) {
        const carrierName = get(carrierData, 'organisation.name', carrierId)
        const invalidStates = [...new Set(invalidRoutes.map(r => r.destinationState))].join(', ')
        throw new Error(
          `Cannot assign carrier "${carrierName}" to routes with destinations in ${invalidStates}. ` +
          `This carrier only operates in: ${carrierStateNames.join(', ')}`
        )
      }
    } catch (err) {
      if (err.message.includes('Cannot assign carrier')) {
        throw err
      }
      console.error('Warning: Could not validate carrier assignment:', err.message)
    }
  }

  async carrierAcceptRoute (routeIds) {
    if (!routeIds || !Array.isArray(routeIds) || !routeIds.length) {
      throw new Error(`Cannot update route: routeIds is required parameter or it can't be empty`)
    }

    // An emptied route (no shipments — cancelled or had all stops moved away) has
    // nothing to deliver, so a carrier can't accept it (issue #20244 / move-stops).
    // Skip such routes and accept the rest, reporting which were skipped.
    const { actionable, skipped } = await this._splitOutEmptiedRoutes(routeIds)
    if (!actionable.length) {
      return { updated: [], skipped }
    }

    const condition = { couchdb_id: actionable }
    const data = {
      accepted: true
    }

    if (this.pgDataAdapter) {
      await this.update({ condition, data })
    } else {
      await this.update(undefined, { condition, data })
    }
    return { updated: actionable, skipped }
  }

  /**
   * Returns the subset of the given route ids that are "emptied" — i.e. have no
   * shipments left, because they were cancelled or had all their stops moved
   * away (issue #20244 / move-stops). Such routes have no program/state/origin
   * to act on, so the workflow restricts what can be done with them.
   *
   * @param {string[]} routeIds - couchdb route ids
   * @returns {Promise<string[]>} the ids with no shipments (empty when no PG/shipment api)
   */
  async _getEmptiedRouteIds (routeIds) {
    const ids = (Array.isArray(routeIds) ? routeIds : [routeIds]).filter(Boolean)
    // The emptiness check is PG-backed. When we can't run it (no PG mode, or no
    // shipment api) we fail open — return [] so callers don't wrongly treat every
    // route as emptied and block legitimate operations.
    if (!ids.length || !this.pgDataAdapter || !get(this.mainApi, 'shipment.getRouteIdsWithShipments')) {
      return []
    }
    const withShipments = new Set(await this.mainApi.shipment.getRouteIdsWithShipments(ids))
    return ids.filter(id => !withShipments.has(id))
  }

  /**
   * Splits route ids into the ones we can act on and the ones that must be
   * skipped because they are "emptied" — no shipments left (cancelled or had all
   * stops moved away, issue #20244 / move-stops). Used by the shipment-requiring
   * bulk actions (forward/approve/assign-carrier/accept) so that one emptied route
   * doesn't fail the whole batch: the actionable routes proceed and the skipped
   * ones are reported back to the caller. `skipped` is always empty when the
   * PG-backed emptiness check can't run (see _getEmptiedRouteIds — it fails open).
   *
   * @param {string[]} routeIds - couchdb route ids
   * @returns {Promise<{ actionable: string[], skipped: string[] }>}
   */
  async _splitOutEmptiedRoutes (routeIds) {
    const ids = (Array.isArray(routeIds) ? routeIds : [routeIds]).filter(Boolean)
    const emptied = new Set(await this._getEmptiedRouteIds(ids))
    return {
      actionable: ids.filter(id => !emptied.has(id)),
      skipped: ids.filter(id => emptied.has(id))
    }
  }

  async updateRouteStatus (routeIds, status) {
    if (!status) {
      throw new Error(`Cannot update route status: status is required parameter`)
    }
    if (!Object.values(RouteStatus).includes(status)) {
      throw new Error(`Cannot update route status: status must be one of ${Object.values(RouteStatus).join(', ')}, got: ${status}`)
    }
    if (!routeIds || !routeIds.length) {
      throw new Error(`Cannot update route status: routeIds is required parameter or it can't be empty`)
    }

    // Only planner can update route status to CANCELLED or IN_REVIEW
    const isPlanner = isPlannerUser(this.state.user)
    const isReviewer = isReviewerUser(this.state.user)

    const plannerRestrictedStatuses = [RouteStatus.CANCELLED, RouteStatus.IN_REVIEW]
    if (!isPlanner && plannerRestrictedStatuses.includes(status)) {
      throw new Error(`Only planner can update route status to ${status}`)
    }

    // An emptied route (no shipments — cancelled or had all stops moved away) has
    // no deliveries to plan, so the only status change allowed on it is cancelling
    // it. For any other transition we skip the emptied routes (instead of failing
    // the whole batch) and advance the rest, reporting which were skipped
    // (issue #20244 / move-stops).
    let idsToUpdate = Array.isArray(routeIds) ? routeIds : [routeIds]
    let skipped = []
    if (status !== RouteStatus.CANCELLED) {
      const split = await this._splitOutEmptiedRoutes(routeIds)
      idsToUpdate = split.actionable
      skipped = split.skipped
      if (!idsToUpdate.length) {
        return { updated: [], skipped }
      }
    }

    // Not every status update allowed
    const possibleStatuses = plannerRestrictedStatuses.includes(status)
      ? [
        // provisional routes can always be cancelled or advanced to in_review
        RouteStatus.PROVISIONAL,
        // if the user has both roles, in_review routes can also be updated to cancelled
        ...(
          isReviewer && isPlanner && status === RouteStatus.CANCELLED
            ? [RouteStatus.IN_REVIEW]
            : []
        )
      ]
      : Object.values(RouteStatus).filter(s => s !== status) // we can't update status to the same value
    const condition = { couchdb_id: idsToUpdate, status: possibleStatuses }
    const now = new Date().toJSON()
    const data = {
      status,
      ...(
        status === RouteStatus.APPROVED
          ? {
            approval_date: now,
            estimated_delivery_date: addDays(now, ESTIMATED_DELIVERY_DATE_DAYS),
            estimated_pickup_date: addHours(now, ESTIMATED_PICKUP_DATE_HOURS)
          }
          : {}
      )
    }
    // In PG mode `update` is the class method (single object arg); in agave mode
    // it's the proxied method (params as the 2nd arg). Mirror assignCarrier.
    if (this.pgDataAdapter) {
      await this.update({ condition, data })
    } else {
      await this.update(undefined, { condition, data })
    }
    return { updated: idsToUpdate, skipped }
  }

  async planRoutesManually ({ bufferData, startDate, endDate, initialStatus = RouteStatus.PROVISIONAL, validateSubOrderId = true }) {
    const SHEET_NAME = 'Warehouse shipment export'
    const workbook = xlsx.read(bufferData, { type: 'array' })
    const allocationSheet = workbook.Sheets[SHEET_NAME]
    if (!allocationSheet) {
      throw new Error(`Worksheet '${SHEET_NAME}' not found`)
    }
    const rows = xlsx.utils.sheet_to_json(allocationSheet)

    // Check for the presence of suborderid in all rows (only if validateSubOrderId is true)
    if (validateSubOrderId) {
      const missingSuborderIds = []
      rows.forEach((row, index) => {
        if (!row.subOrderId) {
          missingSuborderIds.push(row.locationCode)
        }
      })

      if (missingSuborderIds.length > 0) {
        throw new Error(` ${missingSuborderIds.join(', ')} missing subOrderId`)
      }
    }

    const data = rows.reduce((acc, row) => {
      const { route: name, subOrderId, shipmentId } = row
      if (!acc[name]) {
        return {
          ...acc,
          [name]: {
            name,
            subOrderIds: [subOrderId],
            shipmentIds: [shipmentId],
            startDate,
            endDate,
            status: initialStatus
          }
        }
      }
      return {
        ...acc,
        [name]: {
          name,
          subOrderIds: [...acc[name].subOrderIds, subOrderId],
          shipmentIds: [...acc[name].shipmentIds, shipmentId],
          startDate,
          endDate,
          status: initialStatus
        }
      }
    }, {})

    return this.bulkCreate(Object.values(data))
  }

  // Private method
  async _listRoutesFromPg (filter) {
    const { limit, offset } = parsePagination(filter)
    const { results = [] } = await this.pgRouteViewDataAdapter.getList({
      filter,
      ordering: '-created_at',
      limit,
      offset,
      skipCount: true
    }) || {}
    return results
  }

  // Private method — single PG query approach
  // Fetches routes with all decoration data (locations, programs, carrier,
  // funders) pre-JOINed in SQL so the JS layer is a pure synchronous transform.
  //
  // hasVolumetrics — triggers a shipment volume calculation call that currently
  //   queries the db_* cache tables, which were not designed for this purpose.
  //   Only PSM tenants use volume tracking; RMS tenants have no volumes, so
  //   there's no reason for them to pay the cost. Once volume is calculated
  //   from proper data tables instead of the db_* cache tables, this should
  //   become significantly faster and the flag may be removable.
  //
  async _listDecoratedRoutes (filter, { hasVolumetrics = false, includeFunders = false } = {}) {
    const { limit, offset } = parsePagination(filter)
    const { results: routes = [] } = await this.pgRouteViewDataAdapter.getList({
      filter,
      ordering: '-created_at',
      limit,
      offset,
      skipCount: true
    }) || {}
    let decoratedRoutes = transformDecoratedRoutes(routes)

    if (hasVolumetrics) {
      decoratedRoutes = await enrichRoutesWithVolumes(decoratedRoutes, this.mainApi.shipment)
    }

    return decoratedRoutes
  }

  async _addDriverInformation (routes) {
    if (!routes || routes.length === 0) {
      return routes
    }

    try {
      // Routes in this function goes through transformDecoratedRoutes which maps couchdb_id to id
      // meaning `id` here is (route:UUID) and not (UUID)
      const filteredRoutes = routes.map(route => route.id).filter(Boolean)
      if (filteredRoutes.length === 0) {
        return routes
      }
      const drivers = await this.mainApi.user.listUsersByRoutes({
        routes: filteredRoutes
      })

      const driversByRouteId = drivers.reduce((acc, driver) => {
        if (!driver.routes || !driver.routes.length) {
          return acc
        }

        const routeId = get(driver, 'routes[0]')
        const driverName = get(driver, 'fullName') || get(driver, 'name')

        return {
          ...acc,
          [routeId]: driverName
        }
      }, {})

      return routes.map(route => ({
        ...route,
        driverName: driversByRouteId[route.id]
      }))
    } catch (_) {
      console.error('Failed to fetch driver information for routes')
      // Return original routes if driver information fetch fails
      return routes
    }
  }

  async _createRouteFilter ({ inputFilter, isOnline = true }) {
    const getCarrierFilter = async ({ organisationId, requestedState }) => {
      if (!organisationId) {
        throw new Error('Carrier user must have an organisationId')
      }

      const filter = { carrier_id: organisationId }

      try {
        const carrierData = await this.mainApi.carrier.getOne(organisationId)
        const operationalStates = get(carrierData, 'operationalStates', [])

        // Extract state names from operational states
        // Format: "zone:south-south:state:edo" -> "edo"
        const stateNames = operationalStates
          .map(opState => {
            const stateMatch = opState.state && opState.state.match(/:state:([^:]+)/)
            return stateMatch ? stateMatch[1] : null
          })
          .filter(Boolean)

        if (stateNames.length > 0) {
          if (requestedState) {
            // Respect the requested state filter, but constrain it to the carrier's operational states
            const requestedStates = Array.isArray(requestedState) ? requestedState : [requestedState]
            const validStates = requestedStates.filter(s => stateNames.includes(s))
            filter.state = validStates.length > 0 ? validStates : stateNames
          } else {
            filter.state = stateNames
          }
        }
      } catch (err) {
        // If we can't fetch carrier data, fall back to just carrier_id filter
        console.error('Failed to fetch carrier operational states:', err.message)
      }

      return filter
    }
    const getStatusFilterByAccess = ({ statusFilter, isFullAccess, isCarrier }) => {
      // If the user has full access, we return the status filter as is, no restrictions
      if (isFullAccess) {
        return statusFilter ? { status: Array.isArray(statusFilter) ? statusFilter : [statusFilter] } : {}
      }

      const requestedStatusList = statusFilter
        ? Array.isArray(statusFilter)
          ? statusFilter
          : [statusFilter]
        : []
      const prohibitedStatuses = [RouteStatus.PROVISIONAL, RouteStatus.CANCELLED]
      const allowedStatuses = Object.values(RouteStatus).filter((status) => !prohibitedStatuses.includes(status))

      const filterWithoutProhibitedStatuses =
        requestedStatusList.filter((status) => allowedStatuses.includes(status))

      // If the user has limited access and there's no status filter,
      // or they're requesting provisional routes specifically,
      // we return all the statuses except provisional
      if (filterWithoutProhibitedStatuses.length === 0) {
        return {
          status: allowedStatuses
        }
      }

      // If the user has limited access and they're requesting an other status we return the filter as is
      if (!Array.isArray(statusFilter)) {
        return { status: statusFilter }
      }

      // If the user has limited access and they're requesting multiple statuses we remove the provisional status and return the filter as it was
      return { status: statusFilter.some((status) => status !== RouteStatus.PROVISIONAL) ? statusFilter : RouteStatus.APPROVED }
    }

    const getStateFilter = (user = {}) => {
      const location = user.location || {}
      const { level, state } = location

      if (level === 'national') {
        return {}
      }

      if (level === 'state' && state) {
        return { state }
      }

      const warehouseOriginId = getWarehouseOriginIdForUser(user)
      if (warehouseOriginId) {
        return { origin_id: warehouseOriginId }
      }

      // Default to return nothing with location info
      return { origin_id: null, state: null }
    }

    const getProgramFilter = (programs, requestedProgramIds = []) => {
      if (!programs || programs.length === 0) {
        return {}
      }
      // Always return program_ids as an array
      const allProgramsThatTheUserCanAccess = Array.isArray(programs) ? programs : [programs]
      const requestedProgramIdsAsArray = Array.isArray(requestedProgramIds) ? requestedProgramIds : [requestedProgramIds]
      // If there's no specification, return everything
      if (!requestedProgramIdsAsArray || requestedProgramIdsAsArray.length === 0) {
        return { program_ids: allProgramsThatTheUserCanAccess }
      }
      // If there was specification, return them, but only those that the user can access
      return { program_ids: requestedProgramIdsAsArray.filter(programId => allProgramsThatTheUserCanAccess.includes(programId)) }
    }

    const getDriverRouteFilter = (userRoutes) => {
      // Drivers should only see routes that are assigned to them
      // user.routes contains route IDs in format 'route:uuid' or 'route:name'
      // which match the couchdb_id in the database
      if (!userRoutes || !Array.isArray(userRoutes) || userRoutes.length === 0) {
        // If driver has no routes assigned, return empty filter which will result in no routes
        return { couchdb_id: [] }
      }
      return { couchdb_id: userRoutes }
    }

    const getOriginFilter = (originIdFilterValue) => {
      if (!originIdFilterValue) {
        return {}
      }
      try {
        const {_id: originId} = resolveWarehouseByLocationId(originIdFilterValue, null)
        return { origin_id: originId }
      } catch (e) {
        // if we can't resolve, do nothing, proceed with the original
        // filter which will likely return no results but
        // at least won't error out the entire request
        return { origin_id: originIdFilterValue }
      }
    }

    const {
      roles: userRoles = [],
      organisationId = null,
      programs: userPrograms = [],
      routes: userRoutes = []
    } = this.state.user

    const isCarrier = isCarrierUser(this.state.user)
    const isDriver = isDriverUser(this.state.user)

    const { status: statusFilter, program_ids: programIdFilter, origin_id: originIdFilter, ...restFilter } = inputFilter

    let roleFilter = {}
    if (isCarrier) {
      roleFilter = await getCarrierFilter({ organisationId, requestedState: restFilter.state })
    } else if (isDriver) {
      roleFilter = getDriverRouteFilter(userRoutes)
    } else {
      roleFilter = getProgramFilter(userPrograms, programIdFilter)
    }

    return {
      ...restFilter,
      ...getStateFilter(this.state.user),
      ...getStatusFilterByAccess({
        statusFilter,
        isCarrier,
        isFullAccess: userRoles.some(role => role.includes('routePlanning:edit'))
      }),
      ...roleFilter,
      ...getOriginFilter(originIdFilter)
    }
  }

  /**
    * Bulk create routes
    *
    * Auto-generates random route names (format: "Route A##") for any routes
    * that don't have a name provided (only when autoGenerateRouteName is enabled).
    *
    * @param {Array} routeInputs - Array of route objects to create
    * @param {Object} [options] - Optional configuration object.
    * @param {boolean} [options.autoGenerateRouteName=false] - Whether to auto-generate route names.
    * @param {Object} [options.creator=null] - The creator user object of the routes.
    * @returns {Promise<Array>} Promise that resolves to shipment update results
    */
  async bulkCreate (routeInputs, { autoGenerateRouteName = false, creator = null } = {}) {
    const routeData = routeInputs.map(route => {
      if (!route) {
        throw new Error('Route is undefined')
      }

      // Auto-generate name if not provided
      if (!route.name && autoGenerateRouteName) {
        route.name = generateRouteName()
      }

      // Enforce name is required when auto-generation is disabled
      if (!route.name) {
        throw new Error('Route name is required')
      }

      const {
        shipmentIds,
        subOrderIds,
        ...rest
      } = route

      return {
        ...rest,
        organisation_id: creator ? creator.organisationId : null,
        created_by: creator ? creator.name : null
      }
    })

    const routeEntries = await this.create({ routeData })

    // Pair by position: PGAdapter.create() with an array uses Promise.all which preserves
    // input order, so routeEntries[i] corresponds to routeInputs[i].
    // Avoid keyBy(routeEntries, 'name') — two routes in the same LGA created at the same
    // timestamp get identical names, causing keyBy to collapse them and all shipments to
    // be assigned to the same route.
    const updateFunc = this.pgDataAdapter
      ? (params) => this.mainApi.shipment.updateRoute(params)
      : (params) => this.mainApi.shipment.updateRoute(undefined, params)

    const relationUpdatePromises = routeInputs.map(({ shipmentIds }, i) => {
      const routeId = routeEntries[i].couchdb_id
      return updateFunc({ shipmentIds, routeId })
    })

    // Transform the routes to update shipment documents with correct routeId.
    // The bulk-route-assignment handler rebuilds the order and snapshot docs
    // from the message, so the message has to carry the route's creation date
    // next to its id — the handler has no Postgres to look it up in, and the
    // date already on a doc belongs to whichever route it is being moved off
    // (issue #25744).
    const transformedRows = routeInputs.flatMap(({
      subOrderIds = [],
      startDate
    }, i) =>
      subOrderIds.map(subOrderId => ({
        subOrderId,
        routeId: routeEntries[i].couchdb_id,
        routeCreatedAt: toRouteCreatedAt(routeEntries[i].created_at),
        startDate
      }))
    )

    const importPromise = transformedRows.length > 0
      ? this.mainApi.routes.bulkImportLocationRoutes(transformedRows)
      : Promise.resolve()
    return Promise.all([importPromise, ...relationUpdatePromises, routeEntries])
  }

  /**
   * Retrieves route counts based on the provided filter.
   *
   * If an Agave adapter is available and no pgRouteViewDataAdapter is present,
   * it fetches counts from the Agave API using snake_case filters.
   * Otherwise, it uses the pgRouteViewDataAdapter to get counts from Postgres for all statuses
   * or for the specified statuses in the filter.
   *
   * @async
   * @param {Object} [options] - Options object.
   * @param {Object} [options.filter={}] - Filter criteria for routes.
   * @param {string} [options.filter.program] - Program ID to filter routes.
   * @param {string} [options.filter.warehouse] - Warehouse ID to filter routes.
   * @returns {Promise<Object>} An object mapping status names (and 'total') to their respective counts.
   */
  async getRouteCounts ({ filter = {} } = {}) {
    const {
      program: programIdFilter,
      warehouse: warehouseFilter,
      status: inputStatusFilter,
      ...restFilter
    } = filter

    // we intentially leave out the status filter here,
    // because we want to get the counts for all statuses
    const apiFilter = {
      ...restFilter,
      ...(programIdFilter ? { program_ids: [programIdFilter] } : {}),
      ...(warehouseFilter ? { origin_id: warehouseFilter } : {})
    }

    if (!this.pgRouteViewDataAdapter && this.agaveAdapter) {
      const snakeCaseFilter = Object.keys(apiFilter).reduce((acc, key) => {
        if (apiFilter[key] !== undefined) {
          acc[snakeCase(key)] = apiFilter[key]
        }
        return acc
      }, {})
      return this.agaveAdapter.list(`${entityName}/counts`, snakeCaseFilter)
    }
    const userRouteFilter = await this._createRouteFilter({ inputFilter: filter })
    const statusFilter = userRouteFilter.status || []
    const statusesToCheck = statusFilter.length > 0
      ? statusFilter
      : Object.values(RouteStatus)

    return this.pgRouteViewDataAdapter.getCountsByStatus(userRouteFilter, statusesToCheck)
  }

  async list (filter = {}, { isOnline = false, isDriverInfoVisibleToAll = false, includeDetails: includeDetailsOption, hasVolumetrics = false, includeFunders = false } = {}) {
    const { includeDetails: includeDetailsFilter, ...restFilter } = filter
    const includeDetails = includeDetailsOption !== undefined
      ? includeDetailsOption
      : (includeDetailsFilter !== undefined ? includeDetailsFilter !== 'false' : true)

    const isCarrier = isCarrierUser(this.state.user)
    const userPrograms = this.state.user.programs || []
    // If the user is not a carrier and has no programs, we return an empty array
    if (!isCarrier && userPrograms.length === 0 && isOnline) {
      return []
    }

    const getRoutesRequester = (isOnline) => {
      return isOnline ? (
        this.agaveAdapter
          ? (routeFilter) => {
            const snakeCaseFilter = Object.keys(routeFilter).reduce((acc, key) => {
              // if a value is undefined, we don't include it in the filter
              if (routeFilter[key] !== undefined) {
                acc[snakeCase(key)] = routeFilter[key]
              }
              return acc
            }, {})
            if (!includeDetails) {
              snakeCaseFilter.includeDetails = 'false'
            }
            return this.agaveAdapter.list(entityName, snakeCaseFilter)
          }
          : async (routeFilter) => {
            const limitedFilter = await this._createRouteFilter({ inputFilter: routeFilter, isOnline })
            return includeDetails
              ? this._listDecoratedRoutes(limitedFilter, { hasVolumetrics, includeFunders })
              : this._listRoutesFromPg(limitedFilter)
          }
      )
        : (routeFilter) => api.list(this.state, routeFilter)
    }
    try {
      const routes = await getRoutesRequester(isOnline)(restFilter)
      // Only add driver details when we’re online with Agave and the user lookup has a fetch.
      // Some callers (e.g. integration tests) don’t provide state.fetch; without this guard
      // they’d throw before enriching routes, so we skip the driver enrichment in that case.
      const needsToAddDriverInformation = (isCarrier || isDriverInfoVisibleToAll) && isOnline && !!this.agaveAdapter && !!this.state.fetch

      if (needsToAddDriverInformation) {
        return this._addDriverInformation.bind(this)(routes)
      }

      return routes
    } catch (e) {
      throw new Error(`Error listing routes: ${e}`)
    }
  }

  async get (routeId, { isOnline = false, includeDetails = true, isDriverInfoVisibleToAll = false, hasVolumetrics = false } = {}) {
    const getRouteRequester = isOnline =>
      isOnline
        ? (routeId) => this.list({ couchdb_id: routeId }, { isOnline, includeDetails, isDriverInfoVisibleToAll, hasVolumetrics, includeFunders: true })
        : (routeId) => api.get(this.state, routeId)

    try {
      const results = await getRouteRequester(isOnline)(routeId)
      // If we're getting the route from couchdb return the results as they are
      if (!isOnline) {
        return results
      }
      if (!Array.isArray(results)) {
        throw new Error(`Expected an array of results, got: ${typeof results}`)
      }
      if (results.length > 1) {
        throw new Error(`Ambiguous results, only one element expected with id: ${routeId}`)
      }
      return results[0]
    } catch (e) {
      console.error('Error fetching route', e)
      throw e
    }
  }

  async create ({ routeData }) {
    const actualRoutes = Array.isArray(routeData) ? routeData : [routeData]

    return this.pgDataAdapter.create(
      actualRoutes.map(route => {
        const id = route.id || uuid()
        const mappedRoute = {
          status: RouteStatus.PROVISIONAL,
          id,
          couchdb_id: `route:${id}`,
          start_date: route.startDate,
          end_date: route.endDate,
          ...route
        }
        return mappedRoute
      })
    )
  }

  /**
   * Updates records matching a given condition.
   * @param {object} params - The parameters for the update operation.
   * @param {object} params.condition - A filter object used to identify the records to be updated.
   * @param {object} params.data - An object containing the new data to apply to the matched records.
   * @param {object} [params.options={ returnFullObject: false }] - Optional settings for the operation.
   * @param {boolean} [params.options.returnFullObject=false] - If true, the method will fetch and return the
   *                                                            complete updated objects from the view (what you
   *                                                            could expect from a getList call).
   *                                                            If false, it returns the direct result from the
   *                                                            update operation, which may be a partial object (e.g., just the ID).
   * @returns {Promise<Array<object>>} A promise that resolves to an array of updated objects.
   *                                   The content of the objects depends on the `returnFullObject` option.
   */
  async update ({ condition, data, options: { returnFullObject } = { returnFullObject: false } }) {
    const updateResult = await this.pgDataAdapter.updateWhere({ filter: condition }, data)
    if (!returnFullObject) {
      return updateResult
    }
    const { results } = await this.pgRouteViewDataAdapter.getList(
      { filter: { id: updateResult.map(r => r.id) }, skipCount: true }
    )
    return results
  }

  bulkImportLocationRoutes (routes) {
    return this.agaveAdapter.post('location_routes/bulk-save', routes)
  }

  async syncToRDS (routeId) {
    let couchDBRoute
    try {
      couchDBRoute = await this.get(routeId)
    } catch (e) {
      console.error('Error fetching route from couchDB', e)
      throw e
    }
    // The only possible operation is to create new routes,
    // no to update or delete them.
    // The reason not to update is to keep the a clean
    // history of changes, route assignments change but
    // routes do not.
    // Keeping routes that have been deleted in couchDB
    // can also be useful to make sense of route assignment
    // history
    const rdsRoute = toRelationalModel(couchDBRoute)
    return this.pgDataAdapter.upsert(rdsRoute)
  }

  listDeliveryDates (service, refDate) {
    return this.adapter.listDeliveryDates(service, refDate)
  }

  listCountDates (service, refDate) {
    return this.adapter.listCountDates(service, refDate)
  }
}

Object.assign(
  RoutesApi.prototype,
  {
    isRouteEditable,
    RouteStatus
  }
)

async function syncLocationRoute (locationsDB, locationId, routeId, programIds) {
  if (!locationsDB) return
  if (!programIds.length) {
    console.warn(`syncLocationRoute: skipping location doc update for ${locationId} - all affected shipments have null program_id`)
    return
  }

  const routeShortId = routeId && routeId.startsWith('route:') ? routeId.slice(6) : routeId
  const now = new Date().toISOString()
  const MAX_RETRIES = 3
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    let doc
    try {
      doc = await locationsDB.get(locationId)
    } catch (err) {
      if (err.status === 404) return
      throw err
    }

    const programs = doc.programs
    if (!programs) return

    let updated = false

    for (const programId of programIds) {
      const shortProgramId = programId.startsWith('program:') ? programId.slice(8) : programId
      const programKey = programs[shortProgramId] !== undefined ? shortProgramId : programId
      const program = programs[programKey]
      if (!program) continue

      Object.keys(program).forEach(serviceId => {
        const service = program[serviceId]
        if (!service || !Array.isArray(service.routes)) return
        if (transitionRoutePeriod(service.routes, routeShortId, now)) updated = true
      })
    }

    if (!updated) return

    try {
      doc.updatedAt = now
      doc.updatedBy = 'routes/move-stop'
      await locationsDB.put(doc)
      return
    } catch (err) {
      if (err.status === 409 && attempt < MAX_RETRIES - 1) continue
      throw err
    }
  }
}

module.exports = rawMethods
module.exports.RoutesApi = RoutesApi
module.exports.methodsNeedingProxy = methodsNeedingProxy
